let socket;
let reconnectTimer;
let onboardingReconnectTimer;
let onboardingReconnectScheduled = false;
let currentUserId;
let currentOnMessageCallback;
let reconnectAttempts = 0;
let interviewStarted = false;
let onboardingActive = false; // Suppress reconnect loop during onboarding
const MAX_RECONNECT_ATTEMPTS = 15;
const RECONNECT_INTERVAL = 3000; // 3 seconds

const createSocket = (userId, onMessageCallback) => {
  const backendUrl = process.env.REACT_APP_API_URL;

  let effectiveUrl;
  if (backendUrl) {
    // Production path: use configured URL (this is the normal case)
    effectiveUrl = backendUrl;
  } else {
    // Fallback path: only allowed on localhost for SWA emulator mode
    // This defensive check prevents accidentally connecting to wrong backend
    // if production build somehow has missing REACT_APP_API_URL
    const ALLOWED_FALLBACK_HOSTS = ['localhost', '127.0.0.1'];
    const currentHost = window.location.hostname;

    if (!ALLOWED_FALLBACK_HOSTS.includes(currentHost)) {
      throw new Error(
        `[WebSocket] REACT_APP_API_URL is not configured and fallback is not allowed on host '${currentHost}'. ` +
        `Fallback is only permitted on: ${ALLOWED_FALLBACK_HOSTS.join(', ')}`
      );
    }

    // SWA emulator mode: connect directly to local backend (SWA doesn't proxy WebSocket)
    effectiveUrl = process.env.REACT_APP_WS_URL || 'http://localhost:8000';
    console.warn('[WebSocket] Using localhost fallback - this should only happen in local development');
  }

  const wsUrl = effectiveUrl.replace(/^https?:\/\//, '').replace(/\/$/, '');
  const protocol = effectiveUrl.startsWith('https') ? 'wss' : 'ws';

  // Check for demo token (WebSocket API doesn't support custom headers)
  const demoToken = sessionStorage.getItem('demoToken');
  let socketUrl = `${protocol}://${wsUrl}/api/ws/user/${userId}`;
  if (demoToken) {
    socketUrl += `?token=${encodeURIComponent(demoToken)}`;
  }

  socket = new WebSocket(socketUrl);

  socket.onopen = () => {
    console.debug('Connected to WebSocket server');
    reconnectAttempts = 0; // Reset reconnect attempts on successful connection
  };

  socket.onclose = (event) => {
    console.debug(`Disconnected from WebSocket server. Code: ${event.code}, Reason: ${event.reason}`);
    
    // Check if this is a session timeout (after interview started)
    if (interviewStarted && event.code !== 1000) {
      // Send timeout message to UI
      if (currentOnMessageCallback) {
        currentOnMessageCallback({
          type: 'session_timeout',
          message: 'Your session has timed out due to inactivity or technical updates. Your progress has been saved.',
          timestamp: new Date().toISOString()
        });
      }
      return;
    }
    
    // During onboarding, suppress the aggressive reconnect loop. Schedule a single reconnect.
    if (onboardingActive) {
      if (!onboardingReconnectScheduled) {
        onboardingReconnectScheduled = true;
        onboardingReconnectTimer = setTimeout(() => {
          onboardingReconnectScheduled = false;
          if (currentUserId && currentOnMessageCallback) {
            createSocket(currentUserId, currentOnMessageCallback);
          }
        }, RECONNECT_INTERVAL);
      }
      return;
    }
    
    // Don't reconnect if interview has started to prevent duplicate questions
    if (interviewStarted) {
      return;
    }
    
    // Try to reconnect unless it was a normal closure
    if (event.code !== 1000 && reconnectAttempts < MAX_RECONNECT_ATTEMPTS) {
      reconnectAttempts++;
      
      reconnectTimer = setTimeout(() => {
        if (currentUserId && currentOnMessageCallback) {
          createSocket(currentUserId, currentOnMessageCallback);
        }
      }, RECONNECT_INTERVAL);
    }
  };

  socket.onerror = (error) => {
    console.error('WebSocket error:', error);
  };

  socket.onmessage = (event) => {
    try {
      const message = JSON.parse(event.data);
      
      // Track when interview actually starts (first question received)
      if (message.type === 'question' && !interviewStarted) {
        interviewStarted = true;
        onboardingActive = false; // Onboarding completed
      }
      // Track onboarding activity to control reconnect behavior
      if (message.type === 'onboarding_message') {
        onboardingActive = true;
      }
      if (message.type === 'processing_complete' || message.type === 'session_complete' || message.type === 'interview_terminated') {
        onboardingActive = false;
      }
      
      onMessageCallback(message);
    } catch (error) {
      console.error('Error parsing WebSocket message:', error);
    }
  };

  return socket;
};

export const connectSocket = (userId, onMessageCallback) => {
  // Prevent rapid duplicate connections
  if (socket && socket.readyState === WebSocket.CONNECTING) {
    return socket;
  }
  
  // Always disconnect existing socket before creating new one
  // This prevents connection conflicts
  if (socket) {
    socket.close(1000, 'Creating new connection');
    socket = null;
  }

  // Store current connection parameters for reconnection
  currentUserId = userId;
  currentOnMessageCallback = onMessageCallback;
  reconnectAttempts = 0;
  
  // Clear any existing reconnect timer
  if (reconnectTimer) {
    clearTimeout(reconnectTimer);
  }
  if (onboardingReconnectTimer) {
    clearTimeout(onboardingReconnectTimer);
    onboardingReconnectTimer = null;
    onboardingReconnectScheduled = false;
  }

  return createSocket(userId, onMessageCallback);
};

export const disconnectSocket = () => {
  
  // Clear reconnection timer and parameters
  if (reconnectTimer) {
    clearTimeout(reconnectTimer);
    reconnectTimer = null;
  }
  if (onboardingReconnectTimer) {
    clearTimeout(onboardingReconnectTimer);
    onboardingReconnectTimer = null;
    onboardingReconnectScheduled = false;
  }
  
  currentUserId = null;
  currentOnMessageCallback = null;
  reconnectAttempts = 0;
  interviewStarted = false; // Reset interview state
  onboardingActive = false;
  
  if (socket) {
    socket.close(1000, 'Normal closure'); // Use code 1000 for normal closure
    socket = null;
  }
};

export const sendMessage = (message) => {
  if (socket && socket.readyState === WebSocket.OPEN) {
    socket.send(JSON.stringify(message));
  }
};

// Expose onboarding state for components that need cleanup decisions
export const isOnboardingActive = () => onboardingActive;
